import React from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, Calendar, Settings, LogOut, TrendingUp, BookOpen, BookOpenCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import ChangePasswordDialog from './ChangePasswordDialog';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (!['director', 'admin'].includes(user?.role)) {
    return <Navigate to="/" />;
  }

  const isDirector = user?.role === 'director';

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-gradient-to-r from-violet-600 to-indigo-600 border-b border-violet-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 py-3">
            <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo-link">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">WISE Admin</span>
            </Link>

            <div className="flex flex-1 flex-wrap items-center justify-end gap-4">
              {isDirector && (
              <Button
                asChild
                variant={isActive('/admin') && location.pathname === '/admin' ? 'secondary' : 'ghost'}
                className={isActive('/admin') && location.pathname === '/admin' ? 'text-violet-600' : 'text-white hover:bg-white/20'}
                data-testid="admin-nav-dashboard"
              >
                <Link to="/admin">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
              </Button>
              )}

              <Button
                asChild
                variant={isActive('/admin/teachers') ? 'secondary' : 'ghost'}
                className={isActive('/admin/teachers') ? 'text-violet-600' : 'text-white hover:bg-white/20'}
                data-testid="admin-nav-teachers"
              >
                <Link to="/admin/teachers">
                  <Users className="w-4 h-4 mr-2" />
                  Faculty
                </Link>
              </Button>

              {isDirector && (
              <Button
                asChild
                variant={isActive('/admin/bookings') ? 'secondary' : 'ghost'}
                className={isActive('/admin/bookings') ? 'text-violet-600' : 'text-white hover:bg-white/20'}
                data-testid="admin-nav-bookings"
              >
                <Link to="/admin/bookings">
                  <Calendar className="w-4 h-4 mr-2" />
                  Bookings
                </Link>
              </Button>
              )}

              {isDirector && (
              <Button
                asChild
                variant={isActive('/admin/settings') ? 'secondary' : 'ghost'}
                className={isActive('/admin/settings') ? 'text-violet-600' : 'text-white hover:bg-white/20'}
                data-testid="admin-nav-settings"
              >
                <Link to="/admin/settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </Button>
              )}

              {isDirector && (
              <Button
                asChild
                variant={isActive('/admin/syllabus') ? 'secondary' : 'ghost'}
                className={isActive('/admin/syllabus') ? 'text-violet-600' : 'text-white hover:bg-white/20'}
                data-testid="admin-nav-syllabus"
              >
                <Link to="/admin/syllabus">
                  <BookOpenCheck className="w-4 h-4 mr-2" />
                  Syllabus
                </Link>
              </Button>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t border-white/20 pt-3 sm:pt-0 sm:border-t-0 sm:border-l sm:pl-4 sm:ml-2">
                <Button
                  asChild
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  data-testid="admin-staff-console-button"
                >
                  <Link to="/staff">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Console
                  </Link>
                </Button>
                <ChangePasswordDialog dark />
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white hover:bg-white/20"
                  data-testid="admin-logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;